import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
  getPoolsForDoctor,
  addDoctorToPool,
  updatePriority,
} from '../../services/api/pool-manager';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const PoolsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 14px;
`;

const PoolRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  margin-top: 8px;
  background: ${(props) => (props.selected ? '#ede7fa' : '#fbfafe')};
  border: 1px solid ${(props) => (props.selected ? '#c9b8ee' : '#eeecf6')};
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.12s ease;
  :hover {
    background: #f4f1fc;
  }
`;

const PoolMeta = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubLine = styled(RegularText)`
  color: #7a7391;
  font-size: 0.8rem;
  margin-top: 2px;
`;

const Badge = styled.span`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  padding: 4px 10px;
  border-radius: 20px;
  color: white;
  background: ${(props) => (props.active ? '#2fa060' : '#b0aabf')};
`;

const AddRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1eff7;
`;

const Select = styled.select`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 9px 12px;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.85rem;
  flex: 1;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const AddButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.85rem;
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  color: white;
  background: linear-gradient(135deg, #6b4bd6, #4b2e83);
  cursor: pointer;
  :disabled {
    background: #c5c0d4;
    cursor: not-allowed;
  }
`;

const HelperText = styled(RegularText)`
  color: #8a84a0;
  margin-top: 12px;
`;

const PriorityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`;

const PriorityLabel = styled(RegularText)`
  color: #7a7391;
  font-size: 0.78rem;
`;

const PriorityInput = styled.input`
  width: 4rem;
  padding: 4px 8px;
  border: 1px solid #ddd6ee;
  border-radius: 8px;
  font-size: 0.8rem;
  font-family: BrandonText-Medium, Lato, sans-serif;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const SavePriorityBtn = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  padding: 4px 10px;
  border: 1px solid #d9d2ef;
  border-radius: 8px;
  color: #4b2e83;
  background: #f7f4fd;
  cursor: pointer;
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const isActive = (pool) => pool.active === 1 || pool.active === true;

class DoctorPoolsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pools: [],
      loading: false,
      error: null,
      addPoolId: '',
      adding: false,
      priorityEdits: {},
      savingPriorityId: null,
    };
  }

  componentDidMount() {
    this.getPools();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.doctorId !== this.props.doctorId) {
      this.getPools();
    }
  }

  getPools = () => {
    const { doctorId } = this.props;
    if (!doctorId) {
      return;
    }
    this.setState({ loading: true, error: null });
    getPoolsForDoctor(doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          this.setState({ pools: res.pools || [] });
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to fetch pools');
        }
      })
      .catch((err) => {
        const message = err.message || 'Failed to fetch pools';
        toast.error(message);
        this.setState({ pools: [], error: message });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onAddToPool = () => {
    const { doctorId } = this.props;
    const { addPoolId } = this.state;
    if (!addPoolId) {
      toast.error('Choose a pool to add this doctor to.');
      return;
    }
    this.setState({ adding: true });
    addDoctorToPool(addPoolId, doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Doctor added to the pool.');
          this.setState({ addPoolId: '' });
          this.getPools();
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to add to pool');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to add to pool');
      })
      .finally(() => {
        this.setState({ adding: false });
      });
  };

  getPriorityValue = (pool) => {
    const { priorityEdits } = this.state;
    const key = `${pool.poolId}`;
    if (Object.prototype.hasOwnProperty.call(priorityEdits, key)) {
      return priorityEdits[key];
    }
    return pool.priority !== undefined && pool.priority !== null
      ? pool.priority
      : 0;
  };

  onPriorityChange = (poolId, value) => {
    this.setState((prev) => ({
      priorityEdits: { ...prev.priorityEdits, [`${poolId}`]: value },
    }));
  };

  onSavePriority = (pool) => {
    const { doctorId } = this.props;
    const value = parseInt(this.getPriorityValue(pool), 10);
    if (Number.isNaN(value)) {
      toast.error('Enter a valid priority.');
      return;
    }
    this.setState({ savingPriorityId: pool.poolId });
    updatePriority(pool.poolId, doctorId, value, [], null)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Priority updated.');
          this.setState((prev) => {
            const edits = { ...prev.priorityEdits };
            delete edits[`${pool.poolId}`];
            return {
              priorityEdits: edits,
              pools: prev.pools.map((p) =>
                `${p.poolId}` === `${pool.poolId}`
                  ? { ...p, priority: value }
                  : p
              ),
            };
          });
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to update priority'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to update priority');
      })
      .finally(() => {
        this.setState({ savingPriorityId: null });
      });
  };

  render() {
    const { pools, loading, error, addPoolId, adding, savingPriorityId } =
      this.state;
    const { doctorName, selectedPoolId, onSelectPool, allPools } = this.props;

    const memberPoolIds = new Set(pools.map((p) => `${p.poolId}`));
    const availablePools = (allPools || []).filter(
      (p) => !memberPoolIds.has(`${p.poolId}`)
    );

    return (
      <Container>
        <BoldText fontSize="1.05rem">{`Pools · ${doctorName || 'Doctor'}`}</BoldText>
        {loading ? (
          <LoadingComponent />
        ) : error ? (
          <HelperText>{error}</HelperText>
        ) : (
          <React.Fragment>
            <PoolsContainer>
              {pools.map((pool) => (
                <PoolRow
                  key={pool.poolId}
                  selected={
                    selectedPoolId && `${selectedPoolId}` === `${pool.poolId}`
                  }
                  onClick={() => onSelectPool(pool)}
                >
                  <PoolMeta>
                    <BoldText>{pool.poolName}</BoldText>
                    <SubLine>{`Pool ${pool.poolId}`}</SubLine>
                    <PriorityRow onClick={(e) => e.stopPropagation()}>
                      <PriorityLabel>Priority</PriorityLabel>
                      <PriorityInput
                        type="number"
                        value={this.getPriorityValue(pool)}
                        onChange={(e) =>
                          this.onPriorityChange(pool.poolId, e.target.value)
                        }
                      />
                      <SavePriorityBtn
                        disabled={`${savingPriorityId}` === `${pool.poolId}`}
                        onClick={() => this.onSavePriority(pool)}
                      >
                        {`${savingPriorityId}` === `${pool.poolId}`
                          ? '…'
                          : 'Save'}
                      </SavePriorityBtn>
                    </PriorityRow>
                  </PoolMeta>
                  <Badge active={isActive(pool)}>
                    {isActive(pool) ? 'Active' : 'Inactive'}
                  </Badge>
                </PoolRow>
              ))}
              {!pools.length ? (
                <HelperText>Not in any pool yet.</HelperText>
              ) : null}
            </PoolsContainer>

            {availablePools.length ? (
              <AddRow>
                <Select
                  value={addPoolId}
                  onChange={(e) => this.setState({ addPoolId: e.target.value })}
                >
                  <option value="">Add to a pool…</option>
                  {availablePools.map((p) => (
                    <option key={p.poolId} value={p.poolId}>
                      {p.poolName} ({p.poolId})
                    </option>
                  ))}
                </Select>
                <AddButton disabled={adding || !addPoolId} onClick={this.onAddToPool}>
                  {adding ? 'Adding…' : 'Add'}
                </AddButton>
              </AddRow>
            ) : null}
          </React.Fragment>
        )}
      </Container>
    );
  }
}

export default DoctorPoolsList;
